/**
 * BoutPivotAnalysis — the analysis of a bout plan (#480).
 *
 * The server computes every number (`backend/src/lib/planAnalysis.ts`, sent
 * with `GET /brackets/tournament/:id/plan`): the clock of each bout, the rest
 * of each corner that a winner fills, the ring fit, the contests per day and
 * the breaks of the IFMA draw order. This window shows them and computes no
 * rest of its own. Before #480 it found no dependency in a plan (the plan
 * rows reached it without their links), so it reported "No issues
 * detected", and it called a rest under 60 minutes the IFMA limit.
 *
 * The clock times use a start time for each day that the manager enters
 * here. The time is not saved.
 *
 * Only a manager of the tournament opens it (the Bout Plan window).
 */

import { useMemo, useState } from 'react';
import { useI18n, formatDay } from '../../i18n';
import { useLabels } from '../../i18n/labels';
import ModalBackdrop from '../shared/ModalBackdrop';
import ModalHeader from '../shared/ModalHeader';
import ModalBody from '../shared/ModalBody';
import ModalFooter from '../shared/ModalFooter';
import TabBar from '../shared/TabBar';
import { ChartIcon, CalendarIcon, BedIcon, DotIcon, CheckIcon, WarningIcon } from '../shared/Icons';
import type { BoutPlanView, BoutPlanItem, Ring } from '../../types';
import { BADGE_XS } from '../shared/badgeSize';

interface Props {
  plan: BoutPlanView;
  /** Every ring of the tournament: a ring with no bout is a flag. */
  rings: Ring[];
  onClose: () => void;
}

type Level = 'critical' | 'warn' | 'info';
type Tab = 'overview' | 'byday' | 'rest' | 'ifma' | 'flags';

function fmtMins(m: number): string {
  const h = Math.floor(m / 60);
  const mins = Math.round(m % 60);
  if (h === 0) return `${mins}m`;
  return mins > 0 ? `${h}h ${mins}m` : `${h}h`;
}

/** A start time ("HH:MM") plus minutes, as "HH:MM". Past midnight it reads 24:10. */
function clockAt(start: string, minutes: number): string {
  const [h, m] = start.split(':').map(Number);
  const total = h * 60 + m + Math.round(minutes);
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

const dayOf = (d?: string) => (d ? String(d).slice(0, 10) : '');

const RING_COLORS = ['bg-blue-500 text-white', 'bg-teal-500 text-white', 'bg-purple-500 text-white', 'bg-orange-500 text-white'];

function IssueCard({ level, text }: { level: Level; text: string }) {
  const cls = { critical: 'border-l-red-500 bg-red-50', warn: 'border-l-amber-500 bg-amber-50', info: 'border-l-blue-500 bg-blue-50' }[level];
  return <div data-testid="analysis-flag" data-level={level} className={`border-l-4 ${cls} rounded-r-lg px-3 py-2.5 mb-2 text-xs text-gray-700 leading-relaxed`}>{text}</div>;
}

function BarRow({ label, count, text, maxBouts, right, colorClass }: { label: string; count: number; text: string; maxBouts: number; right: string; colorClass: string; }) {
  const pct = maxBouts > 0 ? Math.max(Math.round((count / maxBouts) * 100), 6) : 6;
  return (
    <div className="grid grid-cols-[80px_1fr_110px] gap-2 items-center mb-2 text-xs">
      <div className="font-medium text-gray-500 truncate">{label}</div>
      <div className="bg-gray-100 rounded-sm h-5 overflow-hidden">
        <div className={`h-full min-w-fit rounded-sm flex items-center px-2 font-medium whitespace-nowrap ${colorClass}`} style={{ width: `${pct}%` }}>{text}</div>
      </div>
      <div className="text-right text-gray-400">{right}</div>
    </div>
  );
}

export default function BoutPivotAnalysis({ plan, rings, onClose }: Props) {
  const { t } = useI18n();
  const { stageLabel, divisionLabel } = useLabels();
  const pa = t.planAnalysis;
  const [tab, setTab] = useState<Tab>('overview');
  const [startTimes, setStartTimes] = useState<Record<string, string>>({});
  const minRest = plan.min_rest_minutes;

  const a = useMemo(() => {
    const bouts = plan.bouts;
    const byPos = new Map(bouts.map(b => [b.position, b]));
    const classOf = (b?: BoutPlanItem) => (b ? [divisionLabel(b.gender ?? ''), b.age_group, b.weight_class].filter(Boolean).join(' ') : '');
    const bracketLabel = (id: string) => classOf(bouts.find(b => b.bracket_id === id));
    const dateLabel = (d: string) => formatDay(d, 'EEE dd MMM');

    // Days and rings, on the clock of the server.
    const dates = plan.days.map(d => d.date);
    const days = dates.map(date => {
      const dayBouts = bouts.filter(b => dayOf(b.bout_date) === date);
      const ringIds = [...new Set(dayBouts.map(b => b.ring_id ?? ''))];
      const ringsOfDay = ringIds.map(id => {
        const list = dayBouts.filter(b => (b.ring_id ?? '') === id).sort((x, y) => x.position - y.position);
        const finish = Math.max(0, ...list.map(b => b.end_min ?? 0));
        return { id, name: list[0]?.ring_name ?? '—', list, finish };
      }).sort((x, y) => x.name.localeCompare(y.name));
      const sorted = [...ringsOfDay].sort((x, y) => y.finish - x.finish);
      const gap = sorted.length >= 2 ? sorted[0].finish - sorted[sorted.length - 1].finish : 0;
      return { date, rings: ringsOfDay, gap, heaviest: sorted[0], lightest: sorted[sorted.length - 1], total: dayBouts.length };
    });
    const maxBouts = Math.max(1, ...days.flatMap(d => d.rings.map(r => r.list.length)));

    // The dependencies: every corner that a winner fills.
    const deps = bouts.flatMap(b => b.rests.map(r => {
      const src = byPos.get(r.source_position);
      // A null rest is a night only on another day; else a bout has no clock.
      const status = r.rest_min === null ? (r.other_day ? 'overnight' as const : 'unknown' as const)
        : r.rest_min <= 0 ? 'none' as const : r.rest_min < minRest ? 'short' as const : 'ok' as const;
      return {
        from: r.source_position, fromStage: src?.stage ?? '', to: b.position, toStage: b.stage ?? '',
        cls: classOf(b), ring: src && src.ring_id !== b.ring_id ? `${src.ring_name ?? '?'} → ${b.ring_name ?? '?'}` : (b.ring_name ?? '—'),
        rest: r.rest_min, status,
      };
    })).sort((x, y) => x.to - y.to || x.from - y.from);
    const none = deps.filter(d => d.status === 'none');
    const short = deps.filter(d => d.status === 'short');

    const over3 = plan.contests_per_day.filter(c => c.contests > 3);
    const many = plan.contests_per_day.filter(c => c.contests > 1 && c.contests <= 3);
    const unfit = bouts.filter(b => !b.ring_fits);
    const used = new Set(bouts.map(b => b.ring_id).filter(Boolean));
    const unused = rings.length > 1 ? rings.filter(r => !used.has(r.id)) : [];

    const pair = (d: { from: number; to: number }) => `#${d.from} → #${d.to}`;
    const flags: { level: Level; text: string }[] = [];
    if (none.length) flags.push({ level: 'critical', text: pa.flagNoRest.replace('{n}', String(none.length)).replace('{list}', none.map(pair).join(', ')) });
    if (short.length) flags.push({ level: 'warn', text: pa.flagShortRest.replace('{n}', String(short.length)).replace('{min}', String(minRest)).replace('{list}', short.map(d => `${pair(d)} (${fmtMins(d.rest ?? 0)})`).join(', ')) });
    if (plan.draw_order.length) flags.push({ level: 'warn', text: pa.flagDrawOrder.replace('{n}', String(plan.draw_order.length)) });
    if (over3.length) flags.push({ level: 'warn', text: pa.flagContestsOver.replace('{n}', String(over3.length)).replace('{list}', over3.map(c => `${bracketLabel(c.bracket_id)} ${dateLabel(c.date)} (${c.contests})`).join(', ')) });
    if (unfit.length) flags.push({ level: 'warn', text: pa.flagRingFit.replace('{n}', String(unfit.length)).replace('{list}', unfit.map(b => `#${b.position}`).join(', ')) });
    for (const d of days) {
      if (d.gap > 30 && d.heaviest && d.lightest) {
        flags.push({ level: d.gap > 60 ? 'warn' : 'info', text: pa.flagImbalance.replace('{date}', dateLabel(d.date)).replace('{ring}', d.heaviest.name).replace('{gap}', fmtMins(d.gap)).replace('{other}', d.lightest.name) });
      }
    }
    if (unused.length) flags.push({ level: 'warn', text: pa.flagUnusedRings.replace('{list}', unused.map(r => r.name).join(', ')) });
    if (plan.unmeasured.length) flags.push({ level: 'warn', text: pa.flagUnmeasured.replace('{n}', String(plan.unmeasured.length)).replace('{list}', plan.unmeasured.map(p => `#${p}`).join(', ')) });
    if (many.length) flags.push({ level: 'info', text: pa.flagContestsMany.replace('{n}', String(many.length)).replace('{list}', many.map(c => `${bracketLabel(c.bracket_id)} ${dateLabel(c.date)} (${c.contests})`).join(', ')) });

    return {
      days, maxBouts, deps, none, short, over3, many, unfit, flags, bracketLabel, dateLabel, classOf,
      maxContests: Math.max(0, ...plan.contests_per_day.map(c => c.contests)),
      maxGap: Math.max(0, ...days.map(d => d.gap)),
    };
  }, [plan, rings, minRest, pa, divisionLabel]);

  const flagIcon = a.none.length > 0
    ? <DotIcon className="w-3 h-3 text-red-500" />
    : a.flags.some(f => f.level === 'warn')
    ? <DotIcon className="w-3 h-3 text-yellow-500" />
    : <CheckIcon className="w-3 h-3 text-green-600" />;
  const TABS: { id: Tab; label: React.ReactNode }[] = [
    { id: 'overview', label: pa.tabOverview },
    { id: 'byday', label: pa.tabByDay },
    { id: 'rest', label: pa.tabRest },
    { id: 'ifma', label: pa.tabIfma },
    { id: 'flags', label: <span className="inline-flex items-center gap-1">{pa.tabFlags} {flagIcon}</span> },
  ];
  const flagsOrNone = a.flags.length ? a.flags : [{ level: 'info' as const, text: pa.flagNone }];

  return (
    // #552 — the shared panel, at a fixed height: the height does not jump
    // when the tab changes. It opens on top of the Bout plan; Esc closes this
    // one only (the stack of useModalA11y).
    <ModalBackdrop onClose={onClose} maxWidth="3xl" layout="panel" fullHeight ariaLabel={pa.title}>
        <ModalHeader
          title={<span className="inline-flex items-center gap-1.5"><ChartIcon className="w-4 h-4" /> {pa.title}</span>}
          subtitle={pa.subtitle.replace('{bouts}', String(plan.total_bouts)).replace('{days}', String(plan.days.length))}
        />

        {/* shrink-0: a scroll container in a flex column can shrink to 0;
            the tab bar was 14 px tall on a phone. */}
        <TabBar size="compact" className="shrink-0" testId="analysis-tab" tabs={TABS} activeTab={tab} onChange={setTab} />

        <ModalBody className="space-y-4">
          {tab === 'overview' && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {[
                  { label: pa.cardBouts, val: String(plan.total_bouts), warn: false },
                  { label: pa.cardShortRest.replace('{min}', String(minRest)), val: String(a.none.length + a.short.length), warn: a.none.length + a.short.length > 0 },
                  { label: pa.cardDrawOrder, val: String(plan.draw_order.length), warn: plan.draw_order.length > 0 },
                  { label: pa.cardContests, val: String(a.maxContests), warn: a.maxContests > 3 },
                  { label: pa.cardRingFit, val: String(a.unfit.length), warn: a.unfit.length > 0 },
                  { label: pa.cardImbalance, val: fmtMins(a.maxGap), warn: a.maxGap > 60 },
                ].map(m => (
                  <div key={m.label} className="bg-gray-50 rounded-xl p-3">
                    <div className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">{m.label}</div>
                    <div className={`text-xl font-bold ${m.warn ? 'text-amber-600' : 'text-gray-900'}`}>{m.val}</div>
                  </div>
                ))}
              </div>
              {plan.rings.length > 0 && (
                <div>
                  <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">{pa.ringTotals}</div>
                  {plan.rings.map((r, i) => (
                    <BarRow key={r.ring_id} label={r.ring_name} count={r.bout_count} text={pa.ringBouts.replace('{n}', String(r.bout_count))}
                      maxBouts={Math.max(1, ...plan.rings.map(x => x.bout_count))} right={`~${fmtMins(r.est_minutes)}`} colorClass={RING_COLORS[i % RING_COLORS.length]} />
                  ))}
                </div>
              )}
              <div>
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">{pa.topFlags}</div>
                {flagsOrNone.slice(0, 3).map((f, i) => <IssueCard key={i} level={f.level} text={f.text} />)}
                {a.flags.length > 3 && (
                  <button onClick={() => setTab('flags')} className="text-xs text-blue-600 hover:underline">{pa.moreFlags.replace('{n}', String(a.flags.length - 3))}</button>
                )}
              </div>
            </>
          )}

          {tab === 'byday' && (
            <>
              {a.days.length === 0 && <p className="text-sm text-gray-400 text-center py-8">{pa.noDates}</p>}
              <p className="text-[11px] text-gray-400">{pa.startTimeHint}</p>
              {a.days.map(day => {
                const start = startTimes[day.date];
                return (
                  <div key={day.date} className="mb-4">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <div className="text-xs font-bold uppercase tracking-wider text-gray-500 inline-flex items-center gap-1.5">
                        <CalendarIcon className="w-3.5 h-3.5" /> {formatDay(day.date, 'EEEE, dd MMM yyyy')}
                        <span className="ml-1 text-gray-400 normal-case font-normal">— {pa.ringBouts.replace('{n}', String(day.total))}</span>
                      </div>
                      <label className="text-[11px] text-gray-500 inline-flex items-center gap-1.5 ml-auto">
                        {pa.startTime}
                        <input type="time" data-testid={`analysis-start-${day.date}`} className="input text-xs py-0.5 w-auto"
                          value={start ?? ''} onChange={e => setStartTimes(prev => ({ ...prev, [day.date]: e.target.value }))} />
                      </label>
                    </div>
                    {day.rings.map((r, i) => (
                      <div key={r.id}>
                        <BarRow label={r.name} count={r.list.length} text={pa.ringBouts.replace('{n}', String(r.list.length))} maxBouts={a.maxBouts}
                          right={start ? pa.ringSpan.replace('{start}', start).replace('{end}', clockAt(start, r.finish)) : `~${fmtMins(r.finish)}`}
                          colorClass={RING_COLORS[i % RING_COLORS.length]} />
                        {start && (
                          <div data-testid={`analysis-times-${day.date}-${r.name}`} className="flex flex-wrap gap-1 mb-2 ml-[88px]">
                            {r.list.map(b => (
                              <span key={b.id} className="text-[10px] px-1.5 py-0.5 rounded-sm bg-gray-50 border border-gray-200 text-gray-600">
                                #{b.position} {clockAt(start, b.start_min ?? 0)}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                    {day.rings.length >= 2 && (day.gap <= 10
                      ? <p className="text-[11px] text-green-600 mt-1 inline-flex items-center gap-1"><CheckIcon className="w-3 h-3" /> {pa.balanced.replace('{gap}', fmtMins(day.gap))}</p>
                      : <p className={`text-[11px] mt-1 inline-flex items-center gap-1 ${day.gap > 30 ? 'text-amber-600' : 'text-gray-400'}`}><WarningIcon className="w-3 h-3" /> {pa.gapLast.replace('{gap}', fmtMins(day.gap)).replace('{ring}', day.heaviest?.name ?? '')}</p>)}
                  </div>
                );
              })}
            </>
          )}

          {tab === 'rest' && (
            <>
              {a.deps.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-8">{pa.noDependencies}</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[560px] text-xs">
                    <thead>
                      <tr className="border-b border-gray-100">
                        {[pa.colFrom, pa.colTo, pa.colClass, pa.colRing, pa.colRest, ''].map((h, i) => (
                          <th key={i} className="text-left py-2 px-2 font-semibold text-gray-400 uppercase text-[10px] tracking-wider whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {a.deps.map((d, i) => (
                        <tr key={`${d.from}-${d.to}`} data-testid="analysis-dep-row" data-status={d.status} className={`border-b border-gray-50 ${i % 2 ? 'bg-gray-50/50' : ''}`}>
                          <td className="py-2 px-2 font-medium whitespace-nowrap">#{d.from} <span className="text-gray-400 font-normal">{stageLabel(d.fromStage)}</span></td>
                          <td className="py-2 px-2 font-medium whitespace-nowrap">#{d.to} <span className="text-gray-400 font-normal">{stageLabel(d.toStage)}</span></td>
                          <td className="py-2 px-2 text-gray-600 whitespace-nowrap">{d.cls || '—'}</td>
                          <td className="py-2 px-2 text-gray-500 whitespace-nowrap">{d.ring}</td>
                          <td className="py-2 px-2 whitespace-nowrap">
                            {d.status === 'unknown'
                              ? <span className="text-gray-400">{pa.restUnknown}</span>
                              : d.rest === null
                              ? <span className="text-green-600 inline-flex items-center gap-1"><BedIcon className="w-3.5 h-3.5" /> {pa.overnight}</span>
                              : <span className={d.status === 'ok' ? 'text-green-600' : d.status === 'short' ? 'text-amber-600 font-semibold' : 'text-red-600 font-semibold'}>{d.rest <= 0 ? pa.noRest : pa.restMinutes.replace('{n}', String(d.rest))}</span>}
                          </td>
                          <td className="py-2 px-2">
                            {d.status === 'none' && <span className={`badge ${BADGE_XS} bg-red-50 text-red-700 border-red-200`}>{pa.statusNone}</span>}
                            {d.status === 'short' && <span className={`badge badge-amber ${BADGE_XS}`}>{pa.statusShort}</span>}
                            {(d.status === 'ok' || d.status === 'overnight') && <span className={`badge badge-green ${BADGE_XS}`}>{pa.statusOk}</span>}
                            {d.status === 'unknown' && <span className={`badge badge-gray ${BADGE_XS}`}>{pa.statusUnknown}</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="bg-gray-50 rounded-lg p-3 text-[11px] text-gray-500 leading-relaxed">{pa.restRule.replace('{min}', String(minRest))}</div>
            </>
          )}

          {tab === 'ifma' && (
            <>
              <section>
                <div className="text-xs font-semibold text-gray-600 mb-1">{pa.contestsTitle}</div>
                <p className="text-[11px] text-gray-400 mb-2">{pa.contestsRule}</p>
                {a.over3.length + a.many.length === 0
                  ? <p className="text-xs text-green-600 inline-flex items-center gap-1"><CheckIcon className="w-3 h-3" /> {pa.contestsNone}</p>
                  : [...a.over3, ...a.many].map(c => (
                    <IssueCard key={`${c.bracket_id}-${c.date}`} level={c.contests > 3 ? 'warn' : 'info'}
                      text={pa.contestsRow.replace('{label}', a.bracketLabel(c.bracket_id)).replace('{date}', a.dateLabel(c.date)).replace('{n}', String(c.contests))} />
                  ))}
              </section>
              <section>
                <div className="text-xs font-semibold text-gray-600 mb-2">{pa.drawTitle}</div>
                {plan.draw_order.length === 0
                  ? <p className="text-xs text-green-600 inline-flex items-center gap-1"><CheckIcon className="w-3 h-3" /> {pa.drawNone}</p>
                  : plan.draw_order.map(d => (
                    <IssueCard key={`${d.rule}-${d.position}`} level="warn"
                      text={(d.rule === '16.1' ? pa.drawRule161 : pa.drawRule162).replace('{a}', String(d.position)).replace('{b}', String(d.other_position)).replace('{label}', a.bracketLabel(d.bracket_id))} />
                  ))}
              </section>
              <section>
                <div className="text-xs font-semibold text-gray-600 mb-2">{pa.ringFitTitle}</div>
                {a.unfit.length === 0
                  ? <p className="text-xs text-green-600 inline-flex items-center gap-1"><CheckIcon className="w-3 h-3" /> {pa.ringFitNone}</p>
                  : a.unfit.map(b => (
                    <IssueCard key={b.id} level="warn"
                      text={pa.ringFitRow.replace('{n}', String(b.position)).replace('{label}', a.classOf(b)).split('{ring}').join(b.ring_name ?? '—')} />
                  ))}
              </section>
            </>
          )}

          {tab === 'flags' && flagsOrNone.map((f, i) => <IssueCard key={i} level={f.level} text={f.text} />)}
        </ModalBody>

        <ModalFooter onCancel={onClose} cancelLabel={t.common.close} />
    </ModalBackdrop>
  );
}
